import { useCallback, useMemo } from "react";
import {
  Briefcase,
  Code2,
  CookingPot,
  Ellipsis,
  FileSpreadsheet,
  Route,
  Tv,
} from "lucide-react";
import type { LucideIcon } from "lucide-react";
import { useLocation } from "react-router-dom";
import { useAuth } from "../../../context/AuthContext";
import {
  canAccessPath,
  canShowDesenvolvedorMenu,
  hasAccessToAnyPath,
  hasConferenteBaseFichaAccess,
  hasProdutorFichaAccess,
  PRIMARY_NAV_PATHS,
} from "../../../lib/screenAccess";
import {
  itemsMatchPath,
  type RichNavGroup,
  type RichNavMenuItem,
} from "../../ui/navigation-menu-06";
import {
  NAV_META,
  SISTEMA_MAIN_ITEMS,
  SIDEBAR_ONLY_ITEMS,
  toRichNavLink,
  type HeaderNavLink,
} from "../components/headerNavMeta";
import {
  CONTROLE_PRODUCAO_MENU_GROUPS,
  filterMaisMenuGroups,
  flattenMaisMenuGroups,
  MAIS_MENU_GROUPS,
  RODIZIO_MENU_ITEMS,
  APP_MENU_ITEMS,
  DESENVOLVEDOR_MENU_ITEMS,
} from "../components/maisMenuStructure";
import {
  FUNCOES_MENU_ITEMS,
  RELATORIOS_MENU_ITEMS,
  ROTEIRIZACAO_MENU_ITEMS,
  TV_MENU_ITEMS,
  TV_MENU_ITEMS_ENTREGADOR,
  UNGROUPED_MAIS_TITLES,
} from "../navCatalog";

export type AppNavSection = {
  id: string;
  title: string;
  description?: string;
  icon: LucideIcon;
  items?: HeaderNavLink[];
  groups?: RichNavGroup[];
};

export function useAppNav() {
  const location = useLocation();
  const { user } = useAuth();

  const roleName = user?.role?.nome?.toLowerCase() || "";
  const isPizzaiolo = roleName === "pizzaiolo";
  const isRoterista = roleName === "roterista";
  const isForneiro = roleName === "forneiro";
  const isBordeiro = roleName === "bordeiro";
  const isEntregador = roleName === "entregadores" || roleName === "entregador";
  const shouldHideNavigation = isRoterista || isForneiro || isBordeiro;

  const screenCan = useCallback(
    (path: string) => canAccessPath(user, path),
    [user]
  );

  const tvMenuItemsFiltered = useMemo(
    () => TV_MENU_ITEMS.filter((i) => screenCan(i.to)),
    [screenCan]
  );
  const tvMenuItemsEntregadorFiltered = useMemo(
    () => TV_MENU_ITEMS_ENTREGADOR.filter((i) => screenCan(i.to)),
    [screenCan]
  );
  const appMenuItemsFiltered = useMemo(
    () => APP_MENU_ITEMS.filter((i) => screenCan(i.to)),
    [screenCan]
  );
  const desenvolvedorMenuItemsFiltered = useMemo(
    () =>
      canShowDesenvolvedorMenu(user)
        ? DESENVOLVEDOR_MENU_ITEMS.filter((i) => screenCan(i.to))
        : [],
    [screenCan, user]
  );
  const desenvolvedorRich = useMemo(
    () => desenvolvedorMenuItemsFiltered.map(toRichNavLink),
    [desenvolvedorMenuItemsFiltered]
  );
  const showDesenvolvedor = desenvolvedorRich.length > 0;
  const maisMenuGroupsFiltered = useMemo(
    () =>
      filterMaisMenuGroups(MAIS_MENU_GROUPS, screenCan),
    [screenCan]
  );
  const maisMenuItemsFiltered = useMemo(
    () => flattenMaisMenuGroups(maisMenuGroupsFiltered),
    [maisMenuGroupsFiltered]
  );
  const showMaisMenu =
    maisMenuGroupsFiltered.length > 0 || appMenuItemsFiltered.length > 0;

  const controleProducaoGroupsFiltered = useMemo(
    () =>
      filterMaisMenuGroups(CONTROLE_PRODUCAO_MENU_GROUPS, screenCan, {
        includeFichaDiaria:
          hasConferenteBaseFichaAccess(user) || hasProdutorFichaAccess(user),
      }),
    [screenCan, user]
  );
  const controleProducaoItemsFiltered = useMemo(
    () => flattenMaisMenuGroups(controleProducaoGroupsFiltered),
    [controleProducaoGroupsFiltered]
  );
  const controleProducaoGroups = useMemo((): RichNavGroup[] => {
    return controleProducaoGroupsFiltered.map((group) => {
      if (group.kind === "submenu") {
        return {
          title: group.label,
          items: group.items.map(toRichNavLink),
        };
      }

      const path = group.items[0]?.to ?? "";
      const title = path.includes("ficha-diaria")
        ? "Ficha"
        : path.includes("relatorio")
          ? "Relatórios"
          : "Cadastro";

      return {
        title,
        items: group.items.map(toRichNavLink),
      };
    });
  }, [controleProducaoGroupsFiltered]);
  const showControleProducao = controleProducaoGroups.length > 0;

  const relatoriosMenuItemsFiltered = useMemo(
    () => RELATORIOS_MENU_ITEMS.filter((i) => screenCan(i.to)),
    [screenCan]
  );
  const rodizioMenuItemsFiltered = useMemo(
    () => RODIZIO_MENU_ITEMS.filter((i) => screenCan(i.to)),
    [screenCan]
  );
  const funcoesMenuItemsFiltered = useMemo(
    () => FUNCOES_MENU_ITEMS.filter((i) => screenCan(i.to)),
    [screenCan]
  );
  const showPrimaryNav = useMemo(
    () => hasAccessToAnyPath(user, PRIMARY_NAV_PATHS),
    [user]
  );

  const sistemaItems = useMemo(
    () => SISTEMA_MAIN_ITEMS.filter((item) => screenCan(item.href)),
    [screenCan]
  );
  const sidebarOnlyItems = useMemo(
    () => SIDEBAR_ONLY_ITEMS.filter((item) => screenCan(item.href)),
    [screenCan]
  );
  const relatoriosRich = useMemo(
    () => relatoriosMenuItemsFiltered.map(toRichNavLink),
    [relatoriosMenuItemsFiltered]
  );
  const funcoesRich = useMemo(
    () => funcoesMenuItemsFiltered.map(toRichNavLink),
    [funcoesMenuItemsFiltered]
  );
  const rodizioRich = useMemo(
    () => rodizioMenuItemsFiltered.map(toRichNavLink),
    [rodizioMenuItemsFiltered]
  );
  const roteirizacaoMenuItemsFiltered = useMemo(
    () => ROTEIRIZACAO_MENU_ITEMS.filter((i) => screenCan(i.to)),
    [screenCan]
  );
  const roteirizacaoRich = useMemo(
    () => roteirizacaoMenuItemsFiltered.map(toRichNavLink),
    [roteirizacaoMenuItemsFiltered]
  );
  const showRoteirizacao = roteirizacaoRich.length > 0;
  const tvRich = useMemo(
    () => tvMenuItemsFiltered.map(toRichNavLink),
    [tvMenuItemsFiltered]
  );
  const tvEntregadorRich = useMemo(
    () => tvMenuItemsEntregadorFiltered.map(toRichNavLink),
    [tvMenuItemsEntregadorFiltered]
  );

  const maisGroups = useMemo((): RichNavGroup[] => {
    const groups: RichNavGroup[] = [];
    const ungrouped = new Map<string, HeaderNavLink[]>();

    for (const group of maisMenuGroupsFiltered) {
      if (group.kind === "submenu") {
        groups.push({
          title: group.label,
          items: group.items.map(toRichNavLink),
        });
        continue;
      }

      for (const item of group.items) {
        const title = UNGROUPED_MAIS_TITLES[item.to] ?? "Geral";
        const list = ungrouped.get(title) ?? [];
        list.push(toRichNavLink(item));
        ungrouped.set(title, list);
      }
    }

    ungrouped.forEach((items, title) => {
      groups.push({ title, items });
    });

    if (appMenuItemsFiltered.length > 0) {
      groups.push({
        title: "App",
        items: appMenuItemsFiltered.map(toRichNavLink),
      });
    }

    return groups;
  }, [maisMenuGroupsFiltered, appMenuItemsFiltered]);

  const desktopMenuItems = useMemo((): RichNavMenuItem[] => {
    const items: RichNavMenuItem[] = [];
    const pathname = location.pathname;

    if (isEntregador) {
      if (screenCan("/entregadores")) {
        items.push({
          type: "link",
          label: "Entregadores",
          href: "/entregadores",
          icon: NAV_META["/entregadores"].icon,
          active: pathname === "/entregadores",
        });
      }
      if (tvEntregadorRich.length > 0) {
        items.push({
          type: "grid",
          label: "TV",
          icon: Tv,
          sectionTitle: "TV",
          items: tvEntregadorRich,
          active: itemsMatchPath(pathname, tvEntregadorRich),
        });
      }
      if (showControleProducao) {
        items.push({
          type: "groups",
          label: "Controle de Produção",
          icon: CookingPot,
          groups: controleProducaoGroups,
          active: itemsMatchPath(
            pathname,
            controleProducaoItemsFiltered.map(toRichNavLink)
          ),
        });
      }
      if (showMaisMenu && maisGroups.length > 0) {
        items.push({
          type: "groups",
          label: "Mais",
          icon: Ellipsis,
          groups: maisGroups,
          active:
            itemsMatchPath(pathname, maisMenuItemsFiltered.map(toRichNavLink)) ||
            itemsMatchPath(pathname, appMenuItemsFiltered.map(toRichNavLink)),
        });
      }
      return items;
    }

    if (isPizzaiolo) {
      if (screenCan("/pizzaiolo-integracao")) {
        items.push({
          type: "link",
          label: "Produção Integração",
          href: "/pizzaiolo-integracao",
          icon: NAV_META["/pizzaiolo-integracao"].icon,
          active: pathname === "/pizzaiolo-integracao",
        });
      }
      if (screenCan("/pizzaiolo")) {
        items.push({
          type: "link",
          label: "Produção",
          href: "/pizzaiolo",
          icon: NAV_META["/pizzaiolo"].icon,
          active: pathname === "/pizzaiolo",
        });
      }
      if (showControleProducao) {
        items.push({
          type: "groups",
          label: "Controle de Produção",
          icon: CookingPot,
          groups: controleProducaoGroups,
          active: itemsMatchPath(
            pathname,
            controleProducaoItemsFiltered.map(toRichNavLink)
          ),
        });
      }
      if (showMaisMenu && maisGroups.length > 0) {
        items.push({
          type: "groups",
          label: "Mais",
          icon: Ellipsis,
          groups: maisGroups,
          active:
            itemsMatchPath(pathname, maisMenuItemsFiltered.map(toRichNavLink)) ||
            itemsMatchPath(pathname, appMenuItemsFiltered.map(toRichNavLink)),
        });
      }
      return items;
    }

    if (showPrimaryNav) {
      for (const entry of sistemaItems) {
        items.push({
          type: "link",
          label: entry.title,
          href: entry.href,
          icon: entry.icon,
          active:
            pathname === entry.href || pathname.startsWith(`${entry.href}/`),
        });
      }
    }

    if (relatoriosRich.length > 0) {
      items.push({
        type: "grid",
        label: "Relatórios",
        icon: FileSpreadsheet,
        sectionTitle: "Relatórios",
        items: relatoriosRich,
        active: itemsMatchPath(pathname, relatoriosRich),
      });
    }

    if (funcoesRich.length > 0 || rodizioRich.length > 0 || tvRich.length > 0) {
      const operacaoSide = [...rodizioRich, ...tvRich];
      const hasFuncoes = funcoesRich.length > 0;
      items.push({
        type: "split",
        label: "Operação",
        icon: Briefcase,
        mainTitle: hasFuncoes ? "Funções" : "TV e Rodízio",
        mainItems: hasFuncoes ? funcoesRich : operacaoSide,
        sideTitle: "TV e Rodízio",
        sideItems: hasFuncoes ? operacaoSide : [],
        active:
          itemsMatchPath(pathname, funcoesRich) ||
          itemsMatchPath(pathname, rodizioRich) ||
          itemsMatchPath(pathname, tvRich),
      });
    }

    if (showControleProducao) {
      items.push({
        type: "groups",
        label: "Controle de Produção",
        icon: CookingPot,
        groups: controleProducaoGroups,
        active: itemsMatchPath(
          pathname,
          controleProducaoItemsFiltered.map(toRichNavLink)
        ),
      });
    }

    if (showMaisMenu && maisGroups.length > 0) {
      items.push({
        type: "groups",
        label: "Mais",
        icon: Ellipsis,
        groups: maisGroups,
        active:
          itemsMatchPath(pathname, maisMenuItemsFiltered.map(toRichNavLink)) ||
          itemsMatchPath(pathname, appMenuItemsFiltered.map(toRichNavLink)),
      });
    }

    return items;
  }, [
    location.pathname,
    isEntregador,
    isPizzaiolo,
    screenCan,
    tvEntregadorRich,
    showControleProducao,
    controleProducaoGroups,
    controleProducaoItemsFiltered,
    showMaisMenu,
    maisGroups,
    maisMenuItemsFiltered,
    appMenuItemsFiltered,
    showPrimaryNav,
    sistemaItems,
    relatoriosRich,
    funcoesRich,
    rodizioRich,
    tvRich,
  ]);

  const directLinks = useMemo((): HeaderNavLink[] => {
    if (isEntregador && screenCan("/entregadores")) {
      return [
        toRichNavLink({ label: "Entregadores", to: "/entregadores" }),
        ...sidebarOnlyItems,
      ];
    }
    if (isPizzaiolo) {
      return [
        ...(screenCan("/pizzaiolo-integracao")
          ? [
              toRichNavLink({
                label: "Produção Integração",
                to: "/pizzaiolo-integracao",
              }),
            ]
          : []),
        ...(screenCan("/pizzaiolo")
          ? [toRichNavLink({ label: "Produção", to: "/pizzaiolo" })]
          : []),
        ...sidebarOnlyItems,
      ];
    }
    return [...(showPrimaryNav ? sistemaItems : []), ...sidebarOnlyItems];
  }, [
    isEntregador,
    isPizzaiolo,
    screenCan,
    showPrimaryNav,
    sistemaItems,
    sidebarOnlyItems,
  ]);

  const panelSections = useMemo((): AppNavSection[] => {
    const sections: AppNavSection[] = [];

    if (isEntregador) {
      if (tvEntregadorRich.length > 0) {
        sections.push({
          id: "tv",
          title: "TV",
          description: "Rankings e métricas em tela.",
          icon: Tv,
          items: tvEntregadorRich,
        });
      }
      if (showControleProducao) {
        sections.push({
          id: "controle-producao",
          title: "Controle de Produção",
          description: "Setores, fichas e consumo.",
          icon: CookingPot,
          groups: controleProducaoGroups,
        });
      }
      if (showMaisMenu && maisGroups.length > 0) {
        sections.push({
          id: "mais",
          title: "Mais",
          description: "Cadastros, metas e ferramentas.",
          icon: Ellipsis,
          groups: maisGroups,
        });
      }
      return sections;
    }

    if (isPizzaiolo) {
      if (showControleProducao) {
        sections.push({
          id: "controle-producao",
          title: "Controle de Produção",
          description: "Setores, fichas e consumo.",
          icon: CookingPot,
          groups: controleProducaoGroups,
        });
      }
      if (showMaisMenu && maisGroups.length > 0) {
        sections.push({
          id: "mais",
          title: "Mais",
          description: "Cadastros, metas e ferramentas.",
          icon: Ellipsis,
          groups: maisGroups,
        });
      }
      return sections;
    }

    if (relatoriosRich.length > 0) {
      sections.push({
        id: "relatorios",
        title: "Relatórios",
        description: "Volumes, rankings e análises.",
        icon: FileSpreadsheet,
        items: relatoriosRich,
      });
    }

    const operacaoGroups: RichNavGroup[] = [];
    if (funcoesRich.length > 0) {
      operacaoGroups.push({ title: "Funções", items: funcoesRich });
    }
    if (rodizioRich.length > 0) {
      operacaoGroups.push({ title: "Rodízio", items: rodizioRich });
    }
    if (tvRich.length > 0) {
      operacaoGroups.push({ title: "TV", items: tvRich });
    }
    if (operacaoGroups.length > 0) {
      sections.push({
        id: "operacao",
        title: "Operação",
        description: "Funções, rodízio e telas de TV.",
        icon: Briefcase,
        groups: operacaoGroups,
      });
    }

    if (showControleProducao) {
      sections.push({
        id: "controle-producao",
        title: "Controle de Produção",
        description: "Setores, fichas e consumo.",
        icon: CookingPot,
        groups: controleProducaoGroups,
      });
    }

    if (showRoteirizacao) {
      sections.push({
        id: "roteirizacao",
        title: "Roteirização",
        description: "Rotas manuais e automáticas.",
        icon: Route,
        items: roteirizacaoRich,
      });
    }

    if (showMaisMenu && maisGroups.length > 0) {
      sections.push({
        id: "mais",
        title: "Mais",
        description: "Cadastros, metas e ferramentas.",
        icon: Ellipsis,
        groups: maisGroups,
      });
    }

    if (showDesenvolvedor) {
      sections.push({
        id: "desenvolvedor",
        title: "Desenvolvedor",
        description: "Ferramentas e monitoramento.",
        icon: Code2,
        items: desenvolvedorRich,
      });
    }

    return sections;
  }, [
    isEntregador,
    isPizzaiolo,
    tvEntregadorRich,
    showControleProducao,
    controleProducaoGroups,
    showRoteirizacao,
    roteirizacaoRich,
    showMaisMenu,
    maisGroups,
    showDesenvolvedor,
    desenvolvedorRich,
    relatoriosRich,
    funcoesRich,
    rodizioRich,
    tvRich,
  ]);

  return {
    shouldHideNavigation,
    desktopMenuItems,
    directLinks,
    panelSections,
    pathname: location.pathname,
  };
}
